import { Suspense } from "react";
import { Outlet } from "react-router-dom";
import { Box, Container, useMediaQuery, useTheme } from "@mui/material";
import RecaptchaTerms from "@/components/recaptchaTerms";
import { CartProvider } from "@/context/CartContext";
import { DEFAULT_COMPANY_CONFIG } from "@/const";
import { ShopNavigation } from "@/components/PrpNavigation/shopNavigation";
import { useCompanyConfigContext } from "@/context/CompanyConfigContext";
import { GiftCardCatalogProvider } from "@/context/GiftCardCatalogContext";
import { TravelPackagesProvider } from "@/context/TravelPackagesContext";
import { RedemptionCodeProvider } from "@/context/RedemptionCodeContext";
import { ScrollRestoration } from "@/components/ScrollRestoration";

const ShopLayoutInternal = ({ children }: { children: React.ReactNode }) => {
  const { company } = useCompanyConfigContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <ShopNavigation />
      <Box
        id="main-content"
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          minHeight: "100vh",
          overflow: "auto",
          pb: { xs: 7, md: 0 },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ mt: isMobile ? 2 : 14, mb: isMobile ? 8 : 4 }}
        >
          {children}
        </Container>
        {company && (
          <Container
            maxWidth="lg"
            sx={{ mt: isMobile ? 4 : 0, mb: isMobile ? 4 : 0, fontSize: 12 }}
          >
            The merchants represented are not sponsors of the rewards or
            otherwise affiliated with{" "}
            {company?.settings?.strings?.rewardsName ||
              DEFAULT_COMPANY_CONFIG.REWARDS_NAME}
            . The logos and other identifying marks attached are trademarks of
            and owned by each represented company and/or its affiliates. Please
            visit each company&apos;s website for additional terms and
            conditions.
          </Container>
        )}
        <RecaptchaTerms />
      </Box>
    </Box>
  );
};

export const ShopLayout = () => (
  <CartProvider>
    <RedemptionCodeProvider>
      <ShopLayoutInternal>
        <ScrollRestoration />
        <GiftCardCatalogProvider>
          <TravelPackagesProvider>
            <Suspense fallback={<div>Loading...</div>}>
              <Outlet />
            </Suspense>
          </TravelPackagesProvider>
        </GiftCardCatalogProvider>
      </ShopLayoutInternal>
    </RedemptionCodeProvider>
  </CartProvider>
);
